"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { CheckoutPanel } from "../checkout-panel";
import { CheckoutFooter } from "../checkout-footer";
import { BasketChoiceStep, type BasketChoiceNotice } from "../basket-choice-step";
import { resolveRentalStep, rentalStepBeforeBasketChoice } from "../utils/rental-steps";
import { Calendar } from "../../calendar";
import { Button } from "../../button";
import { cn } from "../../../lib/utils";
import { openingWindowsForWeekday, priceRentalHire } from "@obby/lib";
import type { PricingRule } from "@obby/lib";
import {
  rentalOperatingHoursForTicket,
  rentalStartOptionsForDay,
} from "../utils/rental-start-options";
import {
  rentalDayPoolCap,
  rentalPoolStartIsos,
  rentalPoolCapsByStartMinutes,
} from "../utils/rental-pool-caps";
import type { PoolAvailabilityResponse } from "../../../lib/checkout/pool-availability";
import {
  formatWindowEnd,
  formatWindowRange,
} from "../../../lib/checkout/booking-window";
import { UserFormStep } from "../user-form-step";
// ⚠ From ../user-form-step, NOT ../utils/types: both export a `UserFormAnswer`
// and they differ. utils/types omits `Date` and `number` from `value`, which
// UserFormStep emits for date and rating questions, so the narrower one fails
// to compile the moment a listing uses either.
import type {
  UserFormAnswer,
  UserFormNavState,
  UserFormStepHandle,
} from "../user-form-step";
import {
  formHasRenderableQuestions,
  resolveGuestCollectionMode,
} from "../utils/guest-questions";
import { buildQuestionPages } from "../utils/question-pages";
import {
  pruneAnswersToGuestCount,
  pruneAnswersToRenderedQuestions,
} from "../utils/answer-entries";

/**
 * Self-contained rental checkout flow. Rentals are priced per fraction: the
 * partner sets a fraction size (minutes) + price-per-fraction + min/max
 * fractions on the ticket; the customer picks a date, a grid-aligned start
 * time, a number of fractions, and units (quantity). The item is added to the
 * basket and the existing basket checkout collects guest details + payment.
 * The server (ChargeRental) re-derives duration + price authoritatively from
 * the ticket, so the client price here is only for display/Stripe amount.
 */

type RentalTicket = {
  id: string;
  name: string;
  price: number; // price per fraction, pence
  rentalFractionMinutes?: number | null;
  rentalMinFractions?: number | null;
  rentalMaxFractions?: number | null;
  minTicketsPerOrder?: number | null;
  maxTicketsPerOrder?: number | null;
  /** Whether one unit consumes a seat. The server turns this into the
   *  ticket's numberOfGuestsPerTicket (`affectsAvailability ? 1 : 0`) and
   *  caps additional-guest derivation by it, so it decides whether the
   *  booking form may collect additional-guest identity at all.
   *
   *  Optional here only because the client's ticket payload is untyped
   *  (`ticketTypesV2?: any[]`) and the field arrives through a single
   *  hand-written line in the widget's whitelist projection. The COLUMN is
   *  `Boolean @default(true)` and non-null, so the server never sees it
   *  absent - treat absent as TRUE, or dropping that projection line would
   *  silently stop seat-consuming hires collecting identity. */
  affectsAvailability?: boolean;
  resourcePoolId?: string | null;
  // resourceUnitsPerTicket is deliberately absent: the server's maxTickets is
  // already floor(freeUnits / unitsPerTicket), and a rental order draws on one
  // ticket type, so there are no same-pool siblings to net off. Declaring it
  // here would invite someone to divide by it a second time.
  /** Enabled pricing rules for this ticket, ladder rungs included. Passed
   *  straight to priceRentalHire — the same function the server uses. */
  pricingRules?: PricingRule[];
};

type CheckoutRentalProps = {
  course: any;
  ticketTypesV2?: RentalTicket[];
  currency?: string;
  timezone?: string;
  onClose?: () => void;
  onProceedToBasketCheckout: (itemData: any) => void;
  /**
   * "Add to basket" on the basket-choice step. Receives the SAME item
   * `onProceedToBasketCheckout` gets (one `buildItem()`), so the two paths
   * cannot drift. When the host omits it, the choice step is skipped and
   * the hire proceeds straight to checkout (see the `directCheckout`
   * passed to BasketChoiceStep below): the step itself always renders an
   * "Add to Basket" button, so rendering it without a handler would show
   * a button that does nothing.
   */
  onAddToBasket?: CheckoutRentalProps["onProceedToBasketCheckout"];
  /**
   * When true, the basket-choice step mounts invisibly and fires
   * `onProceedToBasketCheckout` once (BasketChoiceStep's own ref guard) —
   * the school-level "no basket" setting and bundles both arrive this way.
   */
  directCheckout?: boolean;
  /** Why the host refused the last basket choice; shown on the basket choice step. */
  basketConflict?: BasketChoiceNotice | null;
  /** Items already in the basket, shown on the choice step's button. */
  basketItemCount?: number;
  /** Reported on every step change so the host can scroll its own container. */
  onStepChange?: (step: string) => void;
  isEmbedMode?: boolean;
  /**
   * Equipment availability for one rental day. Returns null on any failure,
   * which reads as UNCAPPED — see rental-pool-caps.ts.
   */
  onLoadPoolAvailability?: (params: {
    ticketTypeId: string;
    dateISO: string;
    durationMinutes: number;
    /** The exact starts to price — see rentalPoolStartIsos. */
    startsISO?: string[];
  }) => Promise<PoolAvailabilityResponse | null>;
  /**
   * Every RENTAL ticket id on this listing, from the server. Buyer-independent
   * on purpose — see the field's note in lib/checkout/types.ts. Undefined means
   * unknown, and the slot filter then defers to the full slot list.
   */
  rentalTicketIds?: string[];
  [key: string]: any;
};

// Start-of-day window the grid is generated across. Operating-hours-aware
// slotting is a follow-up; for now we offer the full day on the fraction grid.
// The 06:00-22:00 fallback now lives in @obby/lib as
// RENTAL_FALLBACK_DAY_START_MIN / _END_MIN, applied by readRentalOperatingHours
// only when a listing has NO usable slots configured. It is not the grid.

/** A freshly-mounted UserFormStep nav state - page 0, nothing advanced. */
const FRESH_USER_FORM_NAV: UserFormNavState = {
  isFirst: true,
  isLast: false,
  canAdvance: false,
};

function formatMinutes(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function CheckoutRental(props: CheckoutRentalProps) {
  const {
    course,
    ticketTypesV2 = [],
    currency = "GBP",
    timezone = "Europe/London",
    onClose,
    onProceedToBasketCheckout,
    onAddToBasket,
    directCheckout = false,
    basketConflict = null,
    basketItemCount,
    isEmbedMode,
    onLoadPoolAvailability,
    rentalTicketIds,
    // Attendees. This flow has no participants step, but the school flag
    // still decides whether the system identity questions render at all -
    // see showParticipantsStep below.
    participantsEnabled = false,
    participantFields = [],
    onStepChange,
  } = props;

  const rentalTickets = useMemo(
    () => ticketTypesV2.filter((t) => (t.rentalFractionMinutes ?? 0) > 0),
    [ticketTypesV2],
  );

  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(
    rentalTickets[0]?.id ?? null,
  );
  const ticket = rentalTickets.find((t) => t.id === selectedTicketId) ?? null;

  const fractionMinutes = ticket?.rentalFractionMinutes ?? 60;
  const minFractions = Math.max(1, ticket?.rentalMinFractions ?? 1);
  const maxFractions = ticket?.rentalMaxFractions ?? null;
  const minUnits = Math.max(1, ticket?.minTicketsPerOrder ?? 1);
  const maxUnits = ticket?.maxTicketsPerOrder ?? null;
  const pricePerFraction = ticket?.price ?? 0;

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [startMinutes, setStartMinutes] = useState<number | null>(null);
  const [fractions, setFractions] = useState<number>(minFractions);
  const [units, setUnits] = useState<number>(minUnits);

  const [selectedStep, setSelectedStep] =
    useState<"configure" | "questions" | "basketChoice">("configure");
  const [userFormValues, setUserFormValues] = useState<UserFormAnswer[]>([]);
  const [isUserFormValid, setIsUserFormValid] = useState(false);
  // Footer-driven navigation for a PAGED booking form (legacy "oneAtTime", or
  // "all" with a pageBreakBefore). Without it UserFormStep reports validity as
  // "has the buyer paged to the end", which is false on every page - so the
  // footer Add & checkout sat disabled behind "Please complete all required
  // fields" while the buyer stared at a fully answered page, with only the
  // small inline Next to save them. Same wiring as checkout-regular.
  const userFormRef = useRef<UserFormStepHandle>(null);
  const [userFormNav, setUserFormNav] =
    useState<UserFormNavState>(FRESH_USER_FORM_NAV);

  // The chosen start as an instant. Null until the customer has picked both a
  // date and a time; pricing then falls back to "now", which only matters for
  // time-of-day rules and never for the ladder itself.
  const hireStartsAt = useMemo(() => {
    if (!selectedDate || startMinutes == null) return null;
    const y = selectedDate.getFullYear();
    const mo = selectedDate.getMonth();
    const d = selectedDate.getDate();
    const hh = Math.floor(startMinutes / 60);
    const mm = startMinutes % 60;
    const dateStr = `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")} ${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
    return moment.tz(dateStr, timezone).toDate();
  }, [selectedDate, startMinutes, timezone]);

  // What the footer total is priced against while the hire is half-configured.
  //
  // `hireStartsAt` is null far more often than "before the customer has picked
  // anything": changing the duration calls `setStartMinutes(null)`, and so does
  // the effect that drops a start the new duration no longer fits. So an
  // already-configured hire loses its start every time the customer nudges the
  // stepper, and falling straight back to `new Date()` re-priced it against
  // TODAY — with a PEAK_TIME or date-keyed rule on the ticket, the total
  // visibly flipped to the "now" price and back again as they adjusted.
  //
  // Anchoring to the chosen day instead keeps it stable and on the day they
  // actually picked. It cannot be exact — no hour has been chosen, so an
  // hour-keyed rule still resolves when they pick one — which is the residue
  // recorded as card 8.
  const pricingStartsAt = useMemo(() => {
    if (hireStartsAt) return hireStartsAt;
    if (!selectedDate) return null;
    const y = selectedDate.getFullYear();
    const mo = selectedDate.getMonth();
    const d = selectedDate.getDate();
    const dateStr = `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")} 00:00:00`;
    return moment.tz(dateStr, timezone).toDate();
  }, [hireStartsAt, selectedDate, timezone]);

  const durationMinutes = fractions * fractionMinutes;
  // Priced by the SAME function the server uses (ValidateBasketItems and
  // ChargeRental both call priceRentalHire), so a price ladder cannot mean one
  // thing here and another at charge time. With no ladder configured this is
  // still exactly pricePerFraction x fractions x units.
  const { total, unitPrice } = useMemo(
    () =>
      priceRentalHire({
        ticket: { id: ticket?.id ?? "", price: pricePerFraction },
        pricingRules: ticket?.pricingRules ?? [],
        fractions,
        units,
        startTime: pricingStartsAt ?? new Date(),
        schoolTimezone: timezone,
      }),
    [ticket?.id, ticket?.pricingRules, pricePerFraction, fractions, units, pricingStartsAt, timezone],
  );

  // The listing's REAL opening hours for THIS ticket, per weekday. This used to
  // be a hardcoded 06:00-22:00 grid, so a shop open 09:00-17:00 offered 06:00
  // and 21:30 starts that checkout then refused — and one open past 22:00 could
  // not be booked at all.
  //
  // Narrowed to the slots this ticket is assigned to. `rentalTicketIds` is the
  // listing's whole rental-ticket universe and MUST come from the server: it is
  // what lets selectSlotsForRentalTicket tell "assigned to a different rental
  // ticket" from "names a class ticket", and the ticketTypesV2 this component
  // receives is filtered per buyer. Absent — an older API — it defers to the
  // full slot list, which is the previous behaviour.
  //
  // ⚠ The narrowing is real. On the "Electric Mountain Bike Rental" listing the
  // 1.5-hour ticket is configured 09:30-13:00 and 14:00-17:00, but unfiltered
  // it also inherited the full-day ticket's 09:30-17:00 and the half-day's
  // 13:30-17:00, so it offered 12:30, 13:30 and 15:00 starts. A 12:30 hire
  // returns at 14:00, straddling an hour that ticket is closed for.
  //
  // This makes the widget STRICTER than `windowWithinOperatingHours`, which
  // still tests every enabled slot regardless of assignment. That direction is
  // safe — the picker can only hide a time the server would have allowed, never
  // offer one it refuses. Teaching the server half the same filter is
  // fix/rental-per-slot-ticket-availability's job.
  const operatingHours = useMemo(
    () => rentalOperatingHoursForTicket({ course, ticket, rentalTicketIds }),
    [course, ticket, rentalTicketIds],
  );

  /** Bookable windows for a date; empty means the listing is closed that day. */
  const windowsForDate = React.useCallback(
    (date: Date) => openingWindowsForWeekday(operatingHours, date.getDay()),
    [operatingHours],
  );

  // A picked date can stop being open under this ticket, because opening hours
  // are now PER TICKET rather than listing-wide. Two ways in: switching rental
  // option, and `rentalTicketIds` arriving from the server after the customer
  // already picked a day — the course payload loads asynchronously, so the
  // grid narrows mid-session.
  //
  // Left alone the calendar keeps the day rendered as selected while its own
  // `disabled` predicate greys it out, and the start-time block falls to
  // "This duration doesn't fit the available hours. Try a shorter duration." —
  // which is the wrong reason. The day is closed for this option; no duration
  // would fit.
  //
  // Cleared here rather than in the option button so a date that IS still open
  // survives the switch, and so the late-arriving-universe case is covered too.
  useEffect(() => {
    if (!selectedDate) return;
    if (windowsForDate(selectedDate).length > 0) return;
    setSelectedDate(undefined);
    setStartMinutes(null);
  }, [selectedDate, windowsForDate]);

  // The server's own enforce/defer test — "are hours configured at all", NOT
  // "did we derive any windows". The two differ when every configured slot is
  // malformed, and the server measures its defer on the unfiltered count.
  const hoursConfigured = operatingHours.configuredSlotCount > 0;

  // Hours are configured yet no day yielded a window — so every square is
  // disabled and the customer needs to be told why rather than left staring at
  // a greyed-out calendar.
  //
  // This is a PER-TICKET condition, and the copy says so. Malformed slots
  // cannot reach it: an inverted or zero-length window is rolled forward rather
  // than dropped, so every slot counted in configuredSlotCount also lands in
  // byWeekday. The one thing that CAN empty byWeekday on a configured listing
  // is selectSlotsForRentalTicket — a ticket whose every slot is assigned to a
  // different rental ticket — which is why the message names the option rather
  // than the listing. Switching rental option is the customer's way out, and
  // the picker above stays visible.
  const ticketHasNoDays =
    hoursConfigured && operatingHours.byWeekday.size === 0;

  // Typed explicitly: @obby/lib ships no declarations, so its exports arrive as
  // `any` and the grid's element type would be lost downstream.
  //
  // rentalStartOptionsForDay also drops DST-gap times. Without that, on the
  // spring-forward Sunday a listing open across 01:00-01:59 rendered both
  // "01:30" and "02:30" and resolved the first forward onto the second: the
  // customer was charged for one and emailed the other, and because both
  // entries became one instant those pool units sold twice.
  const startOptions = useMemo<number[]>(() => {
    if (!selectedDate) return [];
    return rentalStartOptionsForDay({
      windows: windowsForDate(selectedDate),
      durationMinutes,
      fractionMinutes,
      date: selectedDate,
      timezone,
    });
  }, [
    selectedDate,
    windowsForDate,
    durationMinutes,
    fractionMinutes,
    timezone,
  ]);

  // A picked start must stay one of the RENDERED options.
  //
  // Every path a customer can take already clears it — switching option,
  // picking a date, moving the duration stepper — so this only fires when the
  // grid changes underneath them, which per-ticket hours made possible for the
  // first time: `operatingHours` now depends on `rentalTicketIds`, and
  // `ensureLoaded` silently refetches the course whenever the buyer's email
  // changes. If the two lookups disagree — a transient failure omits the field,
  // or the partner edits the listing's rental tickets mid-checkout — the grid
  // narrows with no user action behind it.
  //
  // The sibling effect above only covers the day closing ENTIRELY. When the day
  // stays open but loses this start, nothing else catches it: no button renders
  // as selected, yet `startMinutes` still holds the old value and
  // `startFitsUnits` fails OPEN for an instant that was never priced, so
  // Continue stays enabled and buildItem submits a hire at an hour the ticket is
  // closed for — the exact booking the narrowing exists to prevent.
  useEffect(() => {
    if (startMinutes === null) return;
    if (startOptions.includes(startMinutes)) return;
    setStartMinutes(null);
  }, [startMinutes, startOptions]);

  // Equipment availability for the chosen day + duration.
  //
  // The request names every start the picker renders, and the response is
  // joined back on those same instants — see rental-pool-caps.ts for why one
  // anchored grid could not cover a multi-window day.
  //
  // Tagged with the duration and the start set it was fetched FOR. Deriving
  // freshness from component state instead (settledDuration ===
  // durationMinutes) goes true one commit BEFORE the stale response is
  // dropped: the debounce timer renders the two as equal while the old answer
  // is still in state, poolCaps recomputes in that same render, and the wrong
  // grid is painted for at least a frame. A tag on the response cannot lag
  // behind it.
  const [poolResponse, setPoolResponse] = useState<{
    forTicketId: string;
    forDuration: number;
    forStarts: string;
    response: PoolAvailabilityResponse | null;
  } | null>(null);

  // Duration is a stepper, so every click is a distinct duration and a
  // distinct URL — the route's max-age=15 never applies and stepping 1 to 10
  // fractions would fire ten sweeps, nine of them discarded, against a service
  // documented as "never make it per-window". Settle first.
  const [settledDuration, setSettledDuration] = useState(durationMinutes);
  useEffect(() => {
    const t = setTimeout(() => setSettledDuration(durationMinutes), 300);
    return () => clearTimeout(t);
  }, [durationMinutes]);

  // The start set is derived key-first, then re-split, so the array's identity
  // tracks its CONTENT rather than the render. `startOptions` is rebuilt on
  // every step of the duration stepper, and most of those steps produce the
  // same instants; keying the effect on the array itself would clear the caps
  // and re-sweep for an answer it already had. ISO instants never contain a
  // comma, so the round trip is exact.
  const poolStartsKey = useMemo(
    () =>
      selectedDate
        ? rentalPoolStartIsos({ startOptions, date: selectedDate, timezone }).join(",")
        : "",
    [startOptions, selectedDate, timezone],
  );
  const poolStartIsos = useMemo(
    () => (poolStartsKey ? poolStartsKey.split(",") : []),
    [poolStartsKey],
  );

  useEffect(() => {
    // Drop the previous answer FIRST. A longer hire's start options are a
    // SUBSET of a shorter one's, at the same instants, so the surviving keys
    // still match and one-hour caps would be painted onto an eight-hour hire.
    // Switching rental option is the same shape with a different pool. Being
    // briefly uncapped is the safe state; a confidently wrong number is not.
    setPoolResponse(null);
    if (!onLoadPoolAvailability || !ticket?.resourcePoolId || !poolStartsKey) {
      return;
    }
    // The start set derives from the UN-debounced duration, so every step of
    // the stepper re-runs this effect. Firing here would spend a full sweep on
    // an answer tagged with the stale duration, which freshness then discards,
    // followed by the real request 300ms later. Wait for the two to agree
    // instead — the timer re-runs this effect when they do.
    if (durationMinutes !== settledDuration) return;
    const ticketTypeId = ticket.id;
    let cancelled = false;
    (async () => {
      // The prop's contract says it returns null on failure, but that is a
      // promise made by the caller, not something enforced here. try/catch
      // rather than .catch on the result: a loader that validates its
      // arguments and throws SYNCHRONOUSLY never returns a promise for
      // .catch to attach to, and a JS consumer returning a non-thenable would
      // make `.catch` itself a TypeError. Both land here as the uncapped
      // state this module guarantees.
      let res: PoolAvailabilityResponse | null = null;
      try {
        res = await onLoadPoolAvailability({
          ticketTypeId,
          // The route still selects rental mode on `date`; the day's first
          // start names the day as well as anything and keeps that contract
          // unchanged. `startsISO` is what actually decides the windows.
          dateISO: poolStartIsos[0]!,
          durationMinutes: settledDuration,
          startsISO: poolStartIsos,
        });
      } catch {
        res = null;
      }
      if (!cancelled) {
        setPoolResponse({
          forTicketId: ticketTypeId,
          forDuration: settledDuration,
          forStarts: poolStartsKey,
          response: res,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    onLoadPoolAvailability,
    ticket?.id,
    ticket?.resourcePoolId,
    poolStartIsos,
    poolStartsKey,
    settledDuration,
    durationMinutes,
  ]);

  // A response is only valid for the duration and start set it was fetched
  // FOR.
  //
  // Every input the answer depends on is named here, including duration.
  // Lengthening a hire by one fraction does normally drop its latest start and
  // so move the key too, but a cap is a statement about a WINDOW, not about a
  // start — a stale answer's keys matching says nothing about its numbers
  // still being right. Uncapped while the duration settles is the safe state.
  // Ticket id included: switching rental option can leave BOTH the duration
  // and the start set unchanged (same fraction size, same day), and the effect
  // that clears the old answer is passive, so it runs after paint. Without
  // this the previous pool's caps are painted for the new ticket for a frame.
  const freshResponse =
    poolResponse &&
    poolResponse.forTicketId === ticket?.id &&
    poolResponse.forDuration === durationMinutes &&
    poolResponse.forStarts === poolStartsKey
      ? poolResponse.response
      : null;

  const poolCaps = useMemo(
    () =>
      selectedDate
        ? rentalPoolCapsByStartMinutes({
            response: freshResponse,
            startOptions,
            date: selectedDate,
            timezone,
          })
        : new Map<number, number>(),
    [freshResponse, startOptions, selectedDate, timezone],
  );

  // Passed startOptions so a day with any UNKNOWN start reads as uncapped
  // rather than as the maximum of the known subset — see rentalDayPoolCap.
  const dayPoolCap = useMemo(
    () => rentalDayPoolCap(poolCaps, startOptions),
    [poolCaps, startOptions],
  );

  // A date whose best start cannot serve even the minimum order is refused
  // wholesale by the start grid below, and the stepper must not also try to
  // express it: effectiveMaxUnits would sit below minUnits, so Stepper would
  // get max < min, pin the value with both buttons dead, and caption it with
  // a quantity that cannot be selected — under a panel already saying the
  // date has nothing. One statement of the fact, in the grid.
  const dayIsUnservable = dayPoolCap != null && dayPoolCap < minUnits;

  // The stepper's real bound: the ticket's own per-order limit AND the best
  // any start on this day can take. Either may be absent; absent = unbounded.
  const effectiveMaxUnits = useMemo(() => {
    if (maxUnits == null && dayPoolCap == null) return null;
    if (maxUnits == null) return dayPoolCap;
    if (dayPoolCap == null) return maxUnits;
    return Math.min(maxUnits, dayPoolCap);
  }, [maxUnits, dayPoolCap]);

  // The cap can arrive AFTER the customer has already raised the quantity, so
  // clamp down when it does. Never below minUnits — a day that cannot serve
  // even the minimum is refused by the start grid instead, with a message.
  useEffect(() => {
    if (effectiveMaxUnits == null) return;
    setUnits((prev) =>
      prev > effectiveMaxUnits ? Math.max(minUnits, effectiveMaxUnits) : prev,
    );
  }, [effectiveMaxUnits, minUnits]);

  // The quantity AFTER the clamp effect above has had its say. Used ONLY to
  // decide whether an already-picked start should be dropped: both effects run
  // in the same commit and read `units` from the same render, so testing the
  // pre-clamp number there wipes a start the clamp has just made valid again.
  //
  // It must NOT reach the fit test below. buildItem sends the raw `units`, so
  // gating Continue on the clamped number enables the button for the render
  // before the clamp commits — and a click in that frame builds an item for
  // more units than the cap allows, which is the whole thing this prevents.
  const effectiveUnits =
    effectiveMaxUnits == null
      ? units
      : Math.min(units, Math.max(minUnits, effectiveMaxUnits));

  /** Whether a start can serve the chosen quantity. Unknown = yes (fail open). */
  const startFitsUnits = React.useCallback(
    (m: number) => {
      const cap = poolCaps.get(m);
      return cap == null || cap >= units;
    },
    [poolCaps, units],
  );

  // Raising the quantity can invalidate an already-picked start. Drop it
  // rather than leaving a selected-but-disabled button on screen.
  useEffect(() => {
    if (startMinutes === null) return;
    const cap = poolCaps.get(startMinutes);
    if (cap != null && cap < effectiveUnits) setStartMinutes(null);
  }, [startMinutes, poolCaps, effectiveUnits]);

  const canContinue =
    !!ticket &&
    !!selectedDate &&
    startMinutes !== null &&
    fractions >= minFractions &&
    startFitsUnits(startMinutes);

  /**
   * Whether there is a questions step at all.
   *
   * The RENDER predicate, not the button gate: a form of only-optional
   * questions must still get a step so the buyer can answer them. Continue is
   * gated separately on `isUserFormValid`, which UserFormStep computes from
   * required-ness. Both failure modes are documented on
   * formHasRenderableQuestions / formHasRenderableRequiredQuestions.
   *
   * `units` is the guest count for a hire — one answer set per unit — so this
   * can change mid-flow, and can go false: the system identity questions need
   * totalGuests > 1, so a drop from 2 units to 1 can empty the step.
   */
  // Both derived exactly as the regular / term / trial / drop-in flows derive
  // them, so a listing does not answer this question differently depending on
  // which checkout the buyer came through.
  //
  // `showParticipantsStep` is the school's attendees feature AND fields to
  // fill. When it is on, `isQuestionRenderable` suppresses the system identity
  // questions, because identity is the participants step's job. This flow has
  // no participants step, so on such a school a hire collects no
  // additional-guest identity at all.
  //
  // Be precise about why that is the right answer, because it is NOT simply
  // "the same as the other flows": they suppress these questions AND collect
  // identity through the participants step, whereas this flow only suppresses.
  // It is still the right trade-off, on one argument only: the questions are
  // stamped required, so rendering them would hard-gate checkout on a school
  // whose own dashboard shows them as suppressed.
  //
  // Do NOT lean on the admin sweep to make this moot - it is offered when the
  // attendees feature is granted and it asks before running, and an owner can
  // re-add the questions per listing afterwards. So a school that declined it,
  // or re-added, keeps identity questions this flow will not render and has no
  // participants step to replace them: that hire collects no additional-guest
  // identity, and the register shows the buyer alone. Accepted, not hidden.
  // Collecting attendees for a hire is a separate feature, not something to
  // smuggle in through the question form.
  const showParticipantsStep =
    participantsEnabled && participantFields.length > 0;
  const { collectSystemGuests } = resolveGuestCollectionMode(
    course?.userQuestionForm?.questions,
    { formEnabled: course?.userQuestionForm?.isEnabled },
  );

  /**
   * Whether a unit of this hire occupies a SEAT.
   *
   * `processBasketItem` derives a v2 ticket's `numberOfGuestsPerTicket` as
   * `affectsAvailability ? 1 : 0` and caps additional-guest derivation at
   * `count x that`, clamped to at least 1. So on a ticket saved with "Takes up
   * a spot" OFF the server keeps exactly ONE guest however many units are
   * hired, and any identity answer past the buyer is dropped on arrival -
   * surviving as an opaque answer row, never as `Booking.additionalGuests`.
   *
   * That is a reason to stop ASKING for the identity, not a reason to shrink
   * the whole form: the two kinds of per-guest question have different
   * destinations. The system identity ones feed guest derivation and are
   * capped; the owner's own per-guest questions ("Rider height") are stored
   * verbatim as `userInputs` and are never capped, so a 4-bike hire should
   * still ask four times. `collectSystemGuests` is the lever that separates
   * them - `isQuestionRenderable` consults it for identity questions only.
   */
  const collectSystemGuestsForHire =
    collectSystemGuests && ticket?.affectsAvailability !== false;

  const showQuestionsStep = formHasRenderableQuestions(
    course?.userQuestionForm,
    units,
    collectSystemGuestsForHire,
    showParticipantsStep,
  );

  // Whether that form actually pages. Computed from the SAME inputs
  // UserFormStep uses internally so the two can never disagree - gating on
  // `mode` alone would miss the "all" + pageBreakBefore case, which is a
  // current editor feature and not just legacy config.
  const userFormPages = useMemo(
    () =>
      buildQuestionPages(
        [...(course?.userQuestionForm?.questions || [])].sort(
          (a: { order?: number }, b: { order?: number }) =>
            (a.order ?? 0) - (b.order ?? 0),
        ),
        {
          mode: course?.userQuestionForm?.mode || "all",
          totalGuests: units,
          collectSystemGuests: collectSystemGuestsForHire,
          participantsEnabled: showParticipantsStep,
        },
      ),
    [
      course?.userQuestionForm?.questions,
      course?.userQuestionForm?.mode,
      units,
      collectSystemGuestsForHire,
      showParticipantsStep,
    ],
  );
  const isUserFormPaged = userFormPages.length > 1;

  /**
   * The step actually rendered.
   *
   * DERIVED, not corrected in an effect. `units` is clamped down
   * asynchronously when the pool-availability response lands, so the questions
   * step can stop existing while the buyer is standing on it — the same
   * async-flip class as dayIsUnservable. An effect would leave one render in
   * which a step with nothing to show is still on screen; deriving closes that
   * window.
   *
   * It does NOT preserve the buyer's place across a disappearance. Reset 1
   * below fires on this same condition and clears `selectedStep`, on purpose:
   * a step that came back on its own would drop the buyer onto the form with
   * no Continue press. Deriving and that reset are one mechanism, not two.
   */
  // The rules live (and are tested) in utils/rental-steps.ts. In short:
  // "questions" needs the form to have questions; "basketChoice" needs the
  // configure state to still be complete, because the pool-cap clamp can
  // invalidate it asynchronously after the buyer has moved on.
  const step = resolveRentalStep({ selectedStep, showQuestionsStep, canContinue });

  // Two resets, with different triggers. Both adjust state DURING RENDER
  // rather than in an effect: React re-runs the component before the browser
  // paints, so a stale value is never shown. An effect runs AFTER paint, which
  // is exactly the frame a buyer's click lands in.
  // https://react.dev/learn/you-might-not-need-an-effect

  // 1. The buyer's place. Deriving `step` alone masks a vanished questions
  //    step but leaves `selectedStep` on "questions", so nudging the quantity
  //    back up would drop them onto the form again with no Continue press.
  if (step !== selectedStep) {
    setSelectedStep(step);
  }

  // 2. The child's verdicts, whenever the form they judged has changed shape.
  //    `isUserFormValid` and `userFormNav` are UserFormStep's last reports, and
  //    it re-reports through effects — after paint. So on the render where the
  //    shape changes, the footer would still be computing from the old form:
  //    a buyer sitting on the terminal "All done here" view when the pool
  //    response clamps `units` gets the remounted page 1 under an ENABLED
  //    "Add & checkout", for a frame, over questions they have not answered.
  //
  //    Tracked on BOTH parts of the shape, because they move independently:
  //    the page count changes when the identity pages cross the totalGuests > 1
  //    boundary, while `units` alone changes how many guests each per-guest
  //    question demands without touching the count (pages are per QUESTION).
  //
  //    Deliberately NOT reset on every return to configure: a buyer who
  //    finishes the form, presses Back to adjust a start time and comes
  //    straight back has changed nothing, and clearing there painted a frame of
  //    disabled button under the red "complete all required fields" on a form
  //    that was already complete.
  //    Watch EVERY input that decides which questions render, not a proxy for
  //    them. Four do: the guest count, the page count, and the two flags
  //    `isQuestionRenderable` consults. Watching only the first two missed the
  //    case that matters - on an "all"-mode form with no page breaks every
  //    question sits on ONE page, so the count cannot move, and switching
  //    rental option resets `units` to the new ticket's minTicketsPerOrder,
  //    which is unchanged between two options that share it. Both numbers hold
  //    still while the identity questions appear, and the previous visit's
  //    "valid" survives into a form nobody has answered.
  const formShape = {
    guests: units,
    pages: userFormPages.length,
    collectSystemGuests: collectSystemGuestsForHire,
    participants: showParticipantsStep,
  };
  const [judgedFormShape, setJudgedFormShape] = useState(formShape);
  if (
    judgedFormShape.guests !== formShape.guests ||
    judgedFormShape.pages !== formShape.pages ||
    judgedFormShape.collectSystemGuests !== formShape.collectSystemGuests ||
    judgedFormShape.participants !== formShape.participants
  ) {
    setJudgedFormShape(formShape);
    setIsUserFormValid(false);
    setUserFormNav(FRESH_USER_FORM_NAV);
  }

  // Report the step so the host can scroll ITS container back to top, the way
  // every other flow does (checkout-appointment.tsx does exactly this).
  //
  // Deliberately not `scrollCheckoutContentToTop()`: this flow gives
  // CheckoutPanel a `title`, so the panel takes its `hasInfoProps` branch and
  // never mounts CheckoutPanel.Content. In the STANDALONE widget there is then
  // no `data-checkout-content` for that helper to find and it falls through to
  // the first `[class*="overflow-y-auto"]` element in the document, which may
  // be something else entirely. The hosts do not have that problem: each one
  // scrolls its OWN ref'd container (the webcomponents overlay's ref'd div
  // carries both `overflow-y-auto` and `data-checkout-content`), and all four
  // handlers take no argument, so the step NAMES here are private to this file.
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);

  function buildItem() {
    if (!ticket || !selectedDate || startMinutes === null) return null;
    const y = selectedDate.getFullYear();
    const mo = selectedDate.getMonth();
    const d = selectedDate.getDate();
    const hh = Math.floor(startMinutes / 60);
    const mm = startMinutes % 60;
    const dateStr = `${y}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")} ${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:00`;
    const startISO = moment.tz(dateStr, timezone).toISOString();
    const endISO = moment.tz(dateStr, timezone).add(durationMinutes, "minutes").toISOString();
    return {
      type: "booking" as const,
      bookingType: "rental" as const,
      courseId: course._id || course.id,
      courseName: course.title,
      sessionDate: startISO,
      ticketSelections: [
        {
          ticketTypeId: ticket.id,
          ticketTypeName: ticket.name,
          quantity: units,
          // The LADDERED per-unit price, not the ticket's raw per-fraction
          // one. The basket renders this line from `price`/`lineTotal` and the
          // item total from `price` below, so sending the raw figure showed a
          // customer £120 against a line and £90 at the bottom of the same
          // drawer — which reads as a bug or a trick, at the point of payment.
          //
          // They agreed before the ladder existed, because `total` was
          // literally pricePerFraction x fractions x units. The ladder
          // replaced the total and left this behind.
          //
          // `unitPrice` comes straight from priceRentalHire rather than
          // total / units: the engine already computed it, and dividing back
          // out is the rounding trap recorded as card 7 against ChargeRental.
          price: unitPrice,
          lineTotal: total,
        },
      ],
      appointmentData: {
        startTime: startISO,
        endTime: endISO,
        duration: durationMinutes,
        fractions,
      },
      price: total,
      currency,
      // See formatBookingWindow -- the basket drawer has no school config, so
      // the listing's clock travels with the item.
      timezone,
      // Pruned to the guest count this hire actually has: `units` is clamped
      // down asynchronously when the pool-availability response lands, and
      // UserFormStep keeps answers for guests that no longer exist (it never
      // prunes, and it stops accepting parent updates once the buyer types).
      // Sending them would record "Guest 3 - ..." answers against a two-unit
      // hire. Omitted entirely when empty, matching every other flow.
      userInputs: (() => {
        // Two prunes, because there are two ways an answer goes stale and
        // neither implies the other: the guest it belongs to can disappear
        // (the pool clamp lowers `units`), and the QUESTION can stop being
        // asked (switching to a ticket that occupies no seat suppresses the
        // identity questions; so does a school on the attendees feature).
        // Sending the second kind records identity against guests the server
        // will never create.
        const rendered = userFormPages.flatMap(
          (page: { questionIndices: number[] }) => page.questionIndices,
        );
        const live = pruneAnswersToGuestCount(
          pruneAnswersToRenderedQuestions(userFormValues, rendered),
          units,
        );
        return live.length ? live : undefined;
      })(),
      imageUrl: course.images?.[0]?.url,
      isOnline: course.isOnline,
    };
  }

  function handleContinue() {
    const item = buildItem();
    if (!item) return;
    onProceedToBasketCheckout(item);
  }

  // Same builder as handleContinue on purpose: the basket row and the
  // direct-checkout row must be the same item.
  function handleAddToBasket() {
    if (!onAddToBasket) return;
    const item = buildItem();
    if (!item) return;
    onAddToBasket(item);
  }

  const onPagedQuestion = step === "questions" && isUserFormPaged;

  function handleFooterContinue() {
    if (step === "configure" && showQuestionsStep) {
      setSelectedStep("questions");
      return;
    }
    // Paged form: Continue walks the page pointer to the end view first,
    // and only then submits.
    if (onPagedQuestion && !userFormNav.isLast) {
      userFormRef.current?.goNext();
      return;
    }
    // Every configure/questions path ends here; the choice step's own
    // buttons call handleAddToBasket / handleContinue.
    setSelectedStep("basketChoice");
  }

  function handleFooterBack() {
    if (step === "basketChoice") {
      // Not the paged-form reset below: the buyer is stepping back over
      // answers they have already given, not leaving the form.
      setSelectedStep(rentalStepBeforeBasketChoice(showQuestionsStep));
      return;
    }
    // Paged form: Back walks back through the pages, and only leaves the step
    // from the first one - otherwise a buyer three pages in loses every page
    // they had visited.
    //
    // ...unless the CONFIGURE state is what went wrong. The footer then reads
    // "Go back and check the date, duration and start time", and paging
    // backwards one question at a time does not lead there - the instruction
    // would be pointing at a button that does something else. `canContinue`
    // can go false from here without the buyer touching anything: the
    // pool-availability response can drop the chosen start.
    //
    // Leaving this way does not change the form's shape, so the render-phase
    // reset above will not fire. Clear the child's verdicts here instead, or
    // they outlive the mount and the next entry gates on the old page's
    // answer. Scoped to the paged branch, so the single-page path keeps the
    // "nothing changed, nothing to re-check" behaviour it was given.
    if (onPagedQuestion && !userFormNav.isFirst && canContinue) {
      userFormRef.current?.goPrevious();
      return;
    }
    if (onPagedQuestion) {
      setUserFormNav(FRESH_USER_FORM_NAV);
      setIsUserFormValid(false);
    }
    setSelectedStep("configure");
  }

  /**
   * "09:30–12:30" once a start is picked; the caller adds the duration.
   *
   * The grid showed start times and the stepper showed a length, and the
   * customer was left to add them up — on a listing whose hires run past a
   * closing gap that is not a safe sum to ask for.
   *
   * ⚠ The END is computed as a real INSTANT, not by adding minutes to the
   * wall clock. `buildItem` stores `moment.tz(dateStr, tz).add(duration)`, and
   * `ChargeRental` re-derives the same elapsed length from the ticket — so
   * wall-clock arithmetic here disagreed with both across a DST change. A 3h
   * hire from 00:30 on the autumn fall-back day returns at 02:30, not 03:30,
   * and the picker used to promise 03:30 while the basket and the final
   * checkout screen said 02:30. One formula, one answer.
   *
   * Gated on the start still being offered: the reset lives in an effect, so
   * for the one render where a duration change invalidates the current start
   * this would otherwise show a window the grid no longer has.
   */
  const selectedWindow = useMemo(() => {
    if (startMinutes === null || !startOptions.includes(startMinutes)) return null;
    if (!selectedDate) return null;
    const dayStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    const hh = String(Math.floor(startMinutes / 60)).padStart(2, "0");
    const mm = String(startMinutes % 60).padStart(2, "0");
    const start = moment.tz(`${dayStr} ${hh}:${mm}:00`, timezone);
    if (!start.isValid()) return null;
    const end = start.clone().add(durationMinutes, "minutes");
    // Shared with `formatBookingWindow`, so the picker, the basket and the
    // final screen name the return day identically — an overnight hire is
    // ordinary here, not an edge case.
    // ⚠ The START is the raw `hh:mm` the grid button prints, not the moment's.
    // On a spring-forward day a start in the missing hour has no wall clock and
    // moment resolves it an hour on, so these labels would disagree with the
    // button the customer just tapped.
    const startLabel = `${hh}:${mm}`;
    return {
      // The footer's "Hire window" — fully qualified, since it stands alone.
      range: formatWindowRange(start, end, startLabel),
      returnLabel: formatWindowEnd(start, end),
      startLabel,
    };
  }, [startMinutes, startOptions, selectedDate, durationMinutes, timezone]);

  const selectedWindowLabel = selectedWindow?.range ?? null;

  /**
   * The window, but only while the grid is actually offering it.
   *
   * `dayIsUnservable` flips true asynchronously when the pool response lands,
   * and `startFitsUnits` goes false when the unit stepper rises — in both
   * cases the panel replaces the start grid with a "not enough equipment"
   * message and Continue disables, so the footer must stop asserting a
   * bookable hire too. The inline "Returns at" line already checks this; the
   * footer did not.
   */
  const offeredWindowLabel =
    selectedWindowLabel &&
    !dayIsUnservable &&
    startOptions.length > 0 &&
    startMinutes !== null &&
    startFitsUnits(startMinutes)
      ? selectedWindowLabel
      : null;

  const durationLabel = (() => {
    const h = Math.floor(durationMinutes / 60);
    const m = durationMinutes % 60;
    return [h ? `${h}h` : null, m ? `${m}m` : null].filter(Boolean).join(" ") || "0m";
  })();

  return (
    <CheckoutPanel
      title={course?.title}
      thumbnail={course?.images?.[0]?.url}
      currency={currency}
      isOnline={course?.isOnline}
      timezone={timezone}
      onClose={onClose}
      isEmbedMode={isEmbedMode}
    >
      {step === "configure" && (
        <div className="space-y-6 p-1">
          {!ticket ? (
            <p className="text-sm text-muted-foreground">
              No rental options are available for this listing.
            </p>
          ) : (
            <>
              {rentalTickets.length > 1 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Rental option</label>
                  <div className="grid gap-2">
                    {rentalTickets.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => {
                          setSelectedTicketId(t.id);
                          setFractions(Math.max(1, t.rentalMinFractions ?? 1));
                          setUnits(Math.max(1, t.minTicketsPerOrder ?? 1));
                          setStartMinutes(null);
                        }}
                        className={cn(
                          "rounded-lg border-2 p-3 text-left transition",
                          t.id === selectedTicketId
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50",
                        )}
                      >
                        <span className="font-medium">{t.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Date</label>
                {ticketHasNoDays && (
                  <p className="text-sm text-muted-foreground">
                    {/* Always "this hire option", never "this listing". The
                        cause is per-ticket, and `rentalTickets` is the
                        BUYER-filtered list: a buyer who can see only one option
                        because the others are tag-gated or prior-purchase-hidden
                        would otherwise be told the whole listing is shut when it
                        is not. Only the offer to switch depends on the count,
                        because only that part is untrue with nothing to switch
                        to. */}
                    {rentalTickets.length > 1
                      ? "This hire option has no bookable opening hours at the moment. Try another option above, or contact us to arrange a hire."
                      : "This hire option has no bookable opening hours at the moment. Please contact us to arrange a hire."}
                  </p>
                )}
                <Calendar
                  mode="single"
                  captionLayout="dropdown"
                  selected={selectedDate}
                  // The month to OPEN on. react-day-picker resolves the
                  // initial month as `month || defaultMonth || today` and
                  // never looks at `selected`, so without this the calendar
                  // remounts on today. That only became reachable once this
                  // flow grew a second step: coming Back from the questions
                  // step unmounts and remounts this, and a hire booked two
                  // months out came back showing the current month with no
                  // visible selection - while the start grid and the footer
                  // window below still described the date the buyer had
                  // picked. It reads as "my date was lost".
                  defaultMonth={selectedDate}
                  onSelect={(d: Date | undefined) => {
                    setSelectedDate(d);
                    setStartMinutes(null);
                  }}
                  disabled={(date: Date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (date < today) return true;
                    // Closed days aren't selectable at all. Letting one be picked
                    // and then showing an empty time grid reads as a broken page,
                    // and it was every day of the week for a listing that only
                    // opens on Mondays.
                    return windowsForDate(date).length === 0;
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Stepper
                  label={`Duration (${durationLabel})`}
                  sublabel={`${formatMinutes(fractionMinutes)} per unit`}
                  value={fractions}
                  min={minFractions}
                  max={maxFractions ?? undefined}
                  onChange={(v) => {
                    setFractions(v);
                    setStartMinutes(null);
                  }}
                  suffix={fractions === 1 ? "fraction" : "fractions"}
                />
                <Stepper
                  label="Quantity"
                  sublabel={
                    !dayIsUnservable &&
                    dayPoolCap != null &&
                    dayPoolCap < (maxUnits ?? Infinity)
                      ? `up to ${dayPoolCap} available, depending on start time`
                      : undefined
                  }
                  value={units}
                  min={minUnits}
                  max={
                    dayIsUnservable
                      ? (maxUnits ?? undefined)
                      : (effectiveMaxUnits ?? undefined)
                  }
                  onChange={setUnits}
                  suffix={units === 1 ? "unit" : "units"}
                />
              </div>

              {selectedDate && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Start time</label>
                  {startOptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      This duration doesn&apos;t fit the available hours. Try a
                      shorter duration.
                    </p>
                  ) : dayIsUnservable ? (
                    <p className="text-sm text-muted-foreground">
                      There isn&apos;t enough equipment free on this date. Try
                      another date or a shorter hire.
                    </p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {startOptions.map((m) => {
                        const fits = startFitsUnits(m);
                        return (
                          <button
                            key={m}
                            type="button"
                            disabled={!fits}
                            onClick={() => setStartMinutes(m)}
                            className={cn(
                              "rounded-md border py-2 text-sm transition",
                              startMinutes === m
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-border hover:border-primary/50",
                              !fits && "opacity-40 cursor-not-allowed",
                            )}
                          >
                            {formatMinutes(m)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {/* Only alongside a live grid. dayIsUnservable flips async when
                      the pool response lands, and startFitsUnits can go false
                      when the unit stepper rises — either way the panel replaces
                      the grid with a "not enough equipment" message, and this
                      line would sit under it still asserting a bookable hire. */}
                  {selectedWindowLabel &&
                    !dayIsUnservable &&
                    startOptions.length > 0 &&
                    startFitsUnits(startMinutes!) && (
                    <p className="text-sm font-medium">
                      {/* Each fact once. The prefix carries the end AND its
                          day when the hire crosses midnight, so repeating a
                          range here either doubled the date ("Returns at Sat 20
                          Jun 02:00 — 22:00 – Sat 20 Jun 02:00") or, bare, read
                          as no window at all on a day rate ("09:00–09:00"). The
                          start is the one fact the prefix does not carry. */}
                      Returns at {selectedWindow?.returnLabel}
                      <span className="text-muted-foreground font-normal">
                        {" — "}
                        from {selectedWindow?.startLabel} ({durationLabel})
                      </span>
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {step === "basketChoice" && (
        <BasketChoiceStep
          itemName={course.title}
          // The footer's own fully-qualified hire window, with the duration
          // appended — the two never disagree about the window itself.
          dateLabel={selectedWindow ? `${selectedWindow.range} (${durationLabel})` : undefined}
          total={total}
          currency={currency}
          basketItemCount={basketItemCount}
          onAddToBasket={handleAddToBasket}
          onProceedToCheckout={handleContinue}
          // No add-to-basket handler means there is no choice to offer;
          // auto-proceed rather than render a dead "Add to Basket" button.
          directCheckout={directCheckout || !onAddToBasket}
          basketConflict={basketConflict}
        />
      )}
      {step === "questions" && (
        <div className="space-y-6 p-1">
          <UserFormStep
            // Remount when the PAGE SET changes, which on this flow can
            // happen with the buyer standing on the form: the pool response
            // lands (its effect does not depend on `step`, so nothing
            // cancels it), the clamp lowers `units`, and the system identity
            // pages stop rendering because they need totalGuests > 1.
            //
            // UserFormStep never moves `currentPageIndex` when its pages
            // shrink, and it reads `isLast` as `currentPageIndex >=
            // pages.length`. So a buyer on page 5 of 6 dropped to a 3-page
            // form was told "All done here", had validity reported TRUE, and
            // got an enabled Add & checkout over required questions that were
            // never rendered - and nothing revalidates them server-side.
            //
            // Remounting restarts at page 1 with every answer intact
            // (`internalValues` seeds from this `values` prop) and reports
            // invalid until the buyer pages to the end of the real set.
            //
            // That is NOT an unconditional guarantee, and it would be wrong to
            // read it as one. For a paged form UserFormStep reports validity as
            // "the pointer reached the terminal position", not "every page is
            // answered", and a question carrying `behaviorAfterQuestion:
            // "endForm"` sends the pointer straight there - so a listing whose
            // first page ends the form can enable checkout over required
            // questions on later pages that never rendered, and nothing
            // revalidates them server-side. That predates this flow and
            // checkout-regular shares it exactly; fixing it means changing what
            // "complete" means for every footerDrivenNav consumer, which is its
            // own card, not this one.
            //
            // Keyed on the SAME tuple the verdict reset above watches, so the
            // two can never fire apart. That pairing is the whole point: the
            // child re-reports validity through an effect that depends on its
            // `pages` ARRAY, which a units change rebuilds, but it reports nav
            // through one that depends only on the three primitive nav values.
            // Clear both from out here without remounting and a units-only
            // change leaves nav pinned to FRESH forever - Continue dead behind
            // "complete all required fields on this page" on a page that is
            // fully answered, and Back leaving the step instead of paging back.
            // Keying on the page count alone kept the buyer's place on a
            // quantity change; it also unpaired the reset from the remount.
            key={`${formShape.pages}-${formShape.guests}-${formShape.collectSystemGuests}-${formShape.participants}`}
            ref={userFormRef}
            questions={course?.userQuestionForm?.questions || []}
            totalGuests={units}
            collectSystemGuests={collectSystemGuestsForHire}
            participantsEnabled={showParticipantsStep}
            values={userFormValues}
            onChange={setUserFormValues}
            onValidationChange={setIsUserFormValid}
            onNavStateChange={setUserFormNav}
            footerDrivenNav={isUserFormPaged}
            mode={course?.userQuestionForm?.mode || "all"}
          />
        </div>
      )}

      <CheckoutFooter
        label={offeredWindowLabel ? "Hire window" : undefined}
        value={offeredWindowLabel ?? undefined}
        total={total}
        currency={currency}
        showBack={step !== "configure"}
        onBack={handleFooterBack}
        showContinue={step !== "basketChoice"}
        // Every path now lands on the basket-choice step, which carries the
        // "Add to Basket" / "Proceed to Checkout" actions itself; the old
        // "Add & checkout" label promised a checkout Continue no longer does.
        continueText="Continue"
        continueDisabled={
          !canContinue ||
          (onPagedQuestion && !userFormNav.isLast
            ? // Mid-form: the gate is "is THIS page answered", not "is the
              // whole form done" - the latter is false on every page but the
              // last, which is what left Continue dead behind an error.
              !userFormNav.canAdvance
            : step === "questions" && !isUserFormValid)
        }
        onContinue={handleFooterContinue}
        showCancel={!!onClose}
        onCancel={onClose}
        isEmbedMode={isEmbedMode}
        validationMessage={
          !canContinue
            ? // Still the configure-step problem even when the buyer is on the
              // questions step: `units` and the pool cap move asynchronously,
              // and the grid that would fix it is on the other step. Say so.
              step === "questions"
              ? "Go back and check the date, duration and start time"
              : "Pick a date, duration and start time"
            : onPagedQuestion && !userFormNav.isLast
              ? userFormNav.canAdvance
                ? undefined
                : "Please complete all required fields on this page"
              : step === "questions" && !isUserFormValid
                ? "Please complete all required fields"
                : undefined
        }
      />
    </CheckoutPanel>
  );
}

function Stepper({
  label,
  sublabel,
  value,
  min,
  max,
  onChange,
  suffix,
}: {
  label: string;
  sublabel?: string;
  value: number;
  min: number;
  max?: number;
  onChange: (v: number) => void;
  suffix?: string;
}) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(max != null ? Math.min(max, value + 1) : value + 1);
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      {sublabel && <p className="text-xs text-muted-foreground">{sublabel}</p>}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={dec}
          disabled={value <= min}
        >
          −
        </Button>
        <span className="min-w-[2ch] text-center font-medium">{value}</span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={inc}
          disabled={max != null && value >= max}
        >
          +
        </Button>
        {suffix && (
          <span className="text-sm text-muted-foreground">{suffix}</span>
        )}
      </div>
    </div>
  );
}
